import { useEffect, useState } from "react";
import { GasOrder } from "../../types/gasOrder.types";
import { getPaidGasOrdersByStation } from "../../services/gasOrdersService";
import { getStations } from "../../services/stationsService";
import { Station } from "../../types/stations.types";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import {
  PageContainer,
  PageHeader,
  PageTitle,
} from "../../components/PageLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { toast } from "react-toastify";
import { formatIsoToBrDateTimeShort } from "../../utils/functions";

export default function GasOrdersPage() {
  const [selectedStationId, setSelectedStationId] = useState<string>("");
  const [stations, setStations] = useState<Station[]>([]);
  const [orders, setOrders] = useState<GasOrder[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchStations() {
      try {
        const data = await getStations();
        setStations(data);
        if (data.length > 0) {
          setSelectedStationId(data[0].id);
        }
      } catch {
        toast.error("Erro ao carregar postos.");
      }
    }
    fetchStations();
  }, []);

  async function loadOrders(silent = false) {
    if (!selectedStationId) return;
    if (!silent) setLoading(true);
    try {
      const data = await getPaidGasOrdersByStation(selectedStationId);
      setOrders(data);
    } catch {
      if (!silent) toast.error("Erro ao carregar abastecimentos.");
      setOrders((prev) => (silent ? prev : []));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadOrders();
    if (!selectedStationId) return;
    const intervalId = setInterval(() => loadOrders(true), 30 * 1000);
    return () => clearInterval(intervalId);
  }, [selectedStationId]);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Abastecimentos</PageTitle>
        <FormControl size="small" sx={{ minWidth: 250, backgroundColor: "#fff" }}>
          <InputLabel id="station-select-label">Posto</InputLabel>
          <Select
            labelId="station-select-label"
            value={selectedStationId}
            label="Posto"
            onChange={(e) => setSelectedStationId(e.target.value)}
          >
            {stations.length === 0 ? (
              <MenuItem disabled value="">
                Nenhum posto disponível
              </MenuItem>
            ) : (
              stations.map((station) => (
                <MenuItem key={station.id} value={station.id}>
                  {station.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </PageHeader>

      {!selectedStationId ? (
        <p>Selecione um posto para visualizar os abastecimentos.</p>
      ) : loading ? (
        <p>Carregando...</p>
      ) : orders.length === 0 ? (
        <p>Nenhum abastecimento encontrado para este posto.</p>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: "auto", marginTop: "16px" }}>
          <Table sx={{ minWidth: 650 }} aria-label="gas orders table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Bomba / Tipo</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Origem</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Cupom</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Data pagamento</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Quantidade</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                    Bomba {order.gasPump} · {order.type}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{order.source}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{order.couponCode || "—"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {order.updatedAt
                      ? formatIsoToBrDateTimeShort(order.updatedAt)
                      : "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} align="right">
                    {order.quantity.toFixed(2)} L
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} align="right">
                    R$ {order.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
}
